import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, money } from "@/lib/api";

const toCsv = (rows) => {
  const cols = ["code", "status", "event_title", "night_date", "night_label", "ticket_name", "quantity", "total", "refund_status", "refunded_amount", "name", "email", "phone", "created_at"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

const Stat = ({ label, value, id }) => (
  <div className="border border-[#E5DACA] rounded-lg bg-white p-5">
    <p className="font-mono text-[11px] uppercase tracking-widest text-[#6B6157]">{label}</p>
    <p className="mt-2 font-mono text-2xl text-[#A8832F]" data-testid={id}>{value}</p>
  </div>
);

export const AdminBookings = ({ headers, onChange }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/bookings", { headers }).then((r) => setRows(r.data)).catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter((r) => !s || [r.code, r.name, r.email, r.night_date, r.ticket_name].join(" ").toLowerCase().includes(s));
  }, [rows, q]);
  const confirmed = rows.filter((r) => r.status === "confirmed");

  const cancel = async (b) => {
    if (!window.confirm(`Cancel ${b.code}, refund ${money(b.total)} to ${b.name}'s card through Square, and release ${b.quantity} seat(s)?`)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/cancel`, null, { headers });
      toast.success(data.refund_status ? `Cancelled · ${money(data.refunded_amount)} refund ${data.refund_status.toLowerCase()}` : "Booking cancelled");
      load();
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(filtered)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "bookings.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-6 space-y-6" data-testid="admin-bookings">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Confirmed bookings" value={confirmed.length} id="stat-bookings" />
        <Stat label="Guests" value={confirmed.reduce((s, r) => s + r.quantity, 0)} id="stat-guests" />
        <Stat label="Revenue" value={money(confirmed.reduce((s, r) => s + r.total, 0))} id="stat-revenue" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-3.5 h-4 w-4 text-[#6B6157]" /><Input placeholder="Search name, email, code, date" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9" data-testid="bookings-search-input" /></div>
        <button type="button" onClick={exportCsv} data-testid="bookings-export-button" className="inline-flex h-11 items-center gap-2 border border-[#1F1B17] px-4 text-xs uppercase tracking-widest text-[#A8832F] hover:bg-[#F1E9DA]"><Download className="h-4 w-4" />CSV</button>
      </div>
      <div className="overflow-x-auto border border-[#E5DACA]">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Guest</TableHead><TableHead>Night</TableHead><TableHead>Seats</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {filtered.map((b) => (
              <TableRow key={b.id} data-testid={`booking-row-${b.code}`}>
                <TableCell className="font-mono text-[#A8832F]">{b.code}</TableCell>
                <TableCell><div>{b.name}</div><div className="text-xs text-[#6B6157]">{b.email}</div></TableCell>
                <TableCell><div>{b.night_date}</div><div className="text-xs text-[#6B6157]">{b.event_title}</div></TableCell>
                <TableCell>{b.quantity} × {b.ticket_name}</TableCell>
                <TableCell className="font-mono">{money(b.total)}</TableCell>
                <TableCell className={b.status === "confirmed" ? "text-emerald-700" : "text-[#6B6157]"}>
                  {b.status}
                  {b.refund_status && <div className="text-xs text-[#A8832F]" data-testid={`refund-status-${b.code}`}>Refund {money(b.refunded_amount)} · {b.refund_status.toLowerCase()}</div>}
                </TableCell>
                <TableCell>{b.status === "confirmed" && <button type="button" onClick={() => cancel(b)} className="text-xs text-red-600 hover:underline" data-testid={`cancel-booking-${b.code}`}>Cancel</button>}</TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={7} className="py-10 text-center text-[#6B6157]" data-testid="no-bookings-message">No bookings yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
